import { useCallback, useMemo } from 'react';

import { ALIGN_SELECTED_ANNOTATIONS } from '../actions/alignSelectedAnnotations';
import {
  computeMultiSelectionAlignUpdates,
  computeMultiSelectionDistributeUpdates,
  computeSelectionAlignToCanvasUpdates,
  MIN_ALIGN_SELECTION_COUNT,
  MIN_CANVAS_ALIGN_SELECTION_COUNT,
  MIN_DISTRIBUTE_SELECTION_COUNT,
} from '../utils/computeMultiSelectionLayoutUpdates';
import getMultiSelectionClientRects from '../utils/getMultiSelectionClientRects';
import { isAnnotationIdLocked } from '../utils/annotationLockUtils';
import { WATERMARK_ANNOTATION_ID } from '../utils/constants';
import useStore from './useStore';

const useMultiSelectionLayout = () => {
  const {
    dispatch,
    selectionsIds = [],
    annotations = {},
    designLayer,
  } = useStore();

  const alignableSelectionIds = useMemo(
    () =>
      selectionsIds.filter(
        (id) =>
          id !== WATERMARK_ANNOTATION_ID && !isAnnotationIdLocked(annotations, id),
      ),
    [annotations, selectionsIds],
  );

  const selectionCount = alignableSelectionIds.length;
  const canAlign = selectionCount >= MIN_ALIGN_SELECTION_COUNT;
  const canAlignToCanvas = selectionCount >= MIN_CANVAS_ALIGN_SELECTION_COUNT;
  const canDistribute = selectionCount >= MIN_DISTRIBUTE_SELECTION_COUNT;

  const applyLayoutUpdates = useCallback(
    (updates) => {
      if (updates.length === 0) {
        return false;
      }

      dispatch({
        type: ALIGN_SELECTED_ANNOTATIONS,
        payload: { updates },
      });

      return true;
    },
    [dispatch],
  );

  const align = useCallback(
    (alignment) => {
      if (!canAlign) {
        return false;
      }

      const clientRects = getMultiSelectionClientRects(
        designLayer,
        alignableSelectionIds,
      );

      return applyLayoutUpdates(
        computeMultiSelectionAlignUpdates(
          clientRects,
          alignment,
          annotations,
        ),
      );
    },
    [
      alignableSelectionIds,
      annotations,
      applyLayoutUpdates,
      canAlign,
      designLayer,
    ],
  );

  const distribute = useCallback(
    (direction) => {
      if (!canDistribute) {
        return false;
      }

      const clientRects = getMultiSelectionClientRects(
        designLayer,
        alignableSelectionIds,
      );

      return applyLayoutUpdates(
        computeMultiSelectionDistributeUpdates(
          clientRects,
          direction,
          annotations,
        ),
      );
    },
    [
      alignableSelectionIds,
      annotations,
      applyLayoutUpdates,
      canDistribute,
      designLayer,
    ],
  );

  const alignToCanvas = useCallback(
    (alignment) => {
      if (!canAlignToCanvas) {
        return false;
      }

      const clientRects = getMultiSelectionClientRects(
        designLayer,
        alignableSelectionIds,
      );

      return applyLayoutUpdates(
        computeSelectionAlignToCanvasUpdates(
          clientRects,
          alignment,
          annotations,
          designLayer,
        ),
      );
    },
    [
      alignableSelectionIds,
      annotations,
      applyLayoutUpdates,
      canAlignToCanvas,
      designLayer,
    ],
  );

  return {
    align,
    alignToCanvas,
    distribute,
    canAlign,
    canAlignToCanvas,
    canDistribute,
    selectionCount,
  };
};

export default useMultiSelectionLayout;
